/**
 * @file FontWeightSlot.tsx
 * @description Место начертания в окне «Свой шрифт» (phase19.16 Р1-7; SB99;
 * проект uiux §3): «Обычный *», «Жирный», «Курсив», «Жирный курсив».
 *
 * Пусто — «Выбрать файл» (на ПК ещё и перетаскивание, на телефоне подписи о
 * нём нет). Выбрали — «Проверяем файл…», затем «✓ имя файла», образец
 * «Аа Бб Вв» самим файлом и [Убрать]. Предупреждения проверки: нет русских
 * букв, «похоже, это жирное» с [Переложить] [Оставить], тяжёлый файл,
 * плавная толщина. Ошибка — текст и повторный выбор. При догрузке
 * начертания занятое место — только для чтения.
 */

import { Check, Loader2 } from "lucide-react";
import { useRef, useState, type DragEvent } from "react";

import { Button } from "../../ui/button";
import type { SlotState } from "../../../hooks/site-view/useFontUploadForm";
import { useLocalFontSample } from "../../../hooks/site-view/useLocalFontSample";
import {
  FONT_FILE_ACCEPT,
  heavyFontText,
  NO_CYRILLIC_TEXT,
  VARIABLE_FONT_TEXT,
  type FontSlotMeta,
} from "../../../lib/site-view/shop-fonts";
import { cn } from "@/lib/utils";

interface Props {
  meta: FontSlotMeta;
  state: SlotState;
  /** Начертание уже загружено (догрузка) — только чтение. */
  loaded: boolean;
  /** Проверка считает файл другим начертанием, а то место свободно. */
  suggest: FontSlotMeta | null;
  disabled?: boolean;
  onPick: (file: File) => void;
  onClear: () => void;
  onMove: (to: FontSlotMeta) => void;
  onKeep: () => void;
}

const Note = ({ tone, children }: { tone: "warning" | "error"; children: string }) => (
  <p className={cn("type-caption", tone === "warning" ? "text-warning" : "text-destructive")}>{children}</p>
);

export function FontWeightSlot({ meta, state, loaded, suggest, disabled = false, onPick, onClear, onMove, onKeep }: Props) {
  const input = useRef<HTMLInputElement | null>(null);
  const [over, setOver] = useState(false);
  const sample = useLocalFontSample(state.status === "ready" ? state.file : null);
  const label = `${meta.label}${meta.required ? " *" : ""}`;
  const busy = disabled || loaded;

  function onDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setOver(false);
    const file = event.dataTransfer.files[0];
    if (file && !busy) onPick(file);
  }

  const chooseButton = (
    <Button variant="outline" disabled={busy} onClick={() => input.current?.click()} data-choose-file="">
      Выбрать файл
    </Button>
  );

  return (
    <div
      role="group"
      aria-label={meta.label}
      onDragOver={(e) => {
        if (busy) return;
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={onDrop}
      className={cn(
        "flex min-w-0 flex-col gap-2 rounded-xl p-3 [box-shadow:inset_0_0_0_1px_var(--border)]",
        over && "bg-accent",
      )}
    >
      <input
        ref={input}
        type="file"
        accept={FONT_FILE_ACCEPT}
        className="hidden"
        aria-label={`Файл: ${meta.label}`}
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = "";
          if (file) onPick(file);
        }}
      />
      <span className="type-body font-medium text-on-surface">{label}</span>
      {loaded ? (
        <span className="type-caption flex items-center gap-1 text-on-surface-variant">
          <Check className="size-4 text-success" aria-hidden /> Загружено
        </span>
      ) : state.status === "empty" ? (
        <div className="flex flex-wrap items-center gap-2">
          {chooseButton}
          <span className="type-caption hidden text-on-surface-variant md:inline">или перетащите файл сюда</span>
        </div>
      ) : state.status === "checking" ? (
        <span role="status" className="type-caption flex min-h-12 items-center gap-2 text-on-surface-variant">
          <Loader2 className="size-4 animate-spin" aria-hidden /> Проверяем файл…
        </span>
      ) : state.status === "error" ? (
        <div className="flex flex-col gap-2">
          <span className="type-caption truncate text-on-surface-variant">{state.fileName}</span>
          <Note tone="error">{state.message}</Note>
          <div className="flex flex-wrap gap-2">
            {chooseButton}
            <Button variant="ghost" onClick={onClear}>
              Убрать
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <div className="flex min-w-0 items-center gap-2">
            <Check className="size-4 shrink-0 text-success" aria-hidden />
            <span className="type-body min-w-0 flex-1 truncate text-on-surface">{state.file.name}</span>
            <Button variant="ghost" disabled={disabled} onClick={onClear}>
              Убрать
            </Button>
          </div>
          <span className="text-on-surface" style={{ fontFamily: sample ?? undefined, fontSize: 24, lineHeight: 1.3 }}>
            Аа Бб Вв
          </span>
          {state.info.hasCyrillic ? null : <Note tone="warning">{NO_CYRILLIC_TEXT}</Note>}
          {suggest && !state.keepAsIs ? (
            <div className="flex flex-col gap-2">
              <Note tone="warning">{`Похоже, это ${suggest.adjective} начертание, а не ${meta.adjective}.`}</Note>
              <div className="flex flex-wrap gap-2">
                <Button variant="soft" onClick={() => onMove(suggest)}>
                  Переложить в «{suggest.label}»
                </Button>
                <Button variant="ghost" onClick={onKeep}>
                  Оставить
                </Button>
              </div>
            </div>
          ) : null}
          {state.info.heavy ? <Note tone="warning">{heavyFontText(state.info.woff2SizeBytes)}</Note> : null}
          {state.info.variable ? <Note tone="warning">{VARIABLE_FONT_TEXT}</Note> : null}
        </div>
      )}
    </div>
  );
}
